/**
 * Earth Component
 * Main orchestrator component for rendering Earth with dynamic quality and features
 */

import React, { useState, useMemo, useEffect, useRef, memo } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Vector3, Quaternion } from 'three'
import EarthSphere from './EarthSphere'
import Atmosphere from './Atmosphere'
import lodConfig from './config/lodConfig.json'
import { usePerTileLOD } from './hooks/usePerTileLOD'
import { useMaterialCache } from './hooks/useMaterialCache'
import { TILE_GRID } from './textures/tileConfig'
import { useSceneReady } from '../SceneReadyManager'
import { getDeviceInfo } from '../../utils/deviceDetection'

export function Earth({
  radius = 2.5,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  autoRotate = false,
  rotationSpeed = 0.1,
  enableAtmosphere = false,
  opacityOverrideRef = null,
  textureQuality = 'auto',
  onLoad = null,
  onLODUpdate = null, // Callback for LOD updates
  preloadComplete = false, // Flag from PreloadManager
  sunPositionRef = null, // Ref to sun position array, updated imperatively each frame
  artemisActive = false, // Flag for when Artemis mode is active
  onArtemisTargetReached = null, // Callback when Earth reaches Artemis target position
  onRotationUpdate = null, // Callback for current rotation value
  forceLOD = null, // Override quality level e.g. 'LOD_2' when zoomed in on a surface location
  focusTargetRotation = null, // Spin Earth to a specific Y angle then freeze (mirrors Artemis)
  innerGroupRef: externalInnerGroupRef = null, // External ref to inner spinning group — used by EarthSurfaceMarker
}) {
  const [isLoaded, setIsLoaded] = useState(false)
  const { camera } = useThree()

  // Detect device and orientation (memoized)
  const deviceInfo = useMemo(() => getDeviceInfo(), [])
  const isMobile = deviceInfo.isMobile
  
  // Get appropriate LOD config for device and filter out disabled LODs (memoized)
  const lodLevels = useMemo(() => {
    const allLods = isMobile ? lodConfig.mobile.lodLevels : lodConfig.desktop.lodLevels
    return allLods.filter(lod => lod.enabled !== false) // Default to enabled if not specified
  }, [isMobile])

  // Pre-create all materials for instant LOD switching
  // Component initialization
  const { materialCache, isReady: materialsReady } = useMaterialCache(lodLevels, preloadComplete)

  // Register earth/clouds materials into the shared opacity override array used by the chase cam
  useEffect(() => {
    const arr = opacityOverrideRef?.current
    if (!Array.isArray(arr) || !materialsReady) return
    for (const lod of Object.values(materialCache)) {
      if (lod?.earth && !arr.includes(lod.earth)) arr.push(lod.earth)
      if (lod?.clouds && !arr.includes(lod.clouds)) arr.push(lod.clouds)
    }
  }, [opacityOverrideRef, materialsReady, materialCache])

  // FIXED LOD - No dynamic switching (memory leak prevention)
  // forceLOD allows external override for specific locations (e.g. surface zoom)
  const fixedQuality = 'LOD_3'
  const currentQuality = forceLOD || fixedQuality
  
  // Track visible tiles for optimization decisions (kept for monitoring, not switching)
  const tileGridConfig = TILE_GRID['LOD_3']
  const { visibleCount } = usePerTileLOD(
    tileGridConfig,
    radius,
    false // Disable LOD switching
  )

  // Auto-rotation (only on Y-axis for day/night cycle)
  // Use ref for continuous rotation to avoid triggering re-renders every frame
  const spinRotationRef = useRef(0)
  const lastRotationUpdateRef = useRef(0)
  const ROTATION_UPDATE_THROTTLE = 0 // Update every frame — consumers use a ref, no re-render cost

  // Seed earthRotationRef in parent immediately on mount — before the first useFrame cycle fires.
  // This eliminates the one-frame gap where EarthSurfaceMarker reads a stale ref value on first load.
  useEffect(() => {
    if (onRotationUpdate) {
      onRotationUpdate(spinRotationRef.current)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  
  // Ref to access the Earth group's rotation
  const earthGroupRef = useRef()
  const innerGroupLocalRef = useRef()
  
  // Callback ref for inner group — assigns to both local and external refs
  const setInnerGroupRef = useMemo(() => (node) => {
    innerGroupLocalRef.current = node
    if (externalInnerGroupRef) externalInnerGroupRef.current = node
  }, [externalInnerGroupRef])
  
  // Artemis mode: simple Y-axis rotation target (in radians)
  const targetYRotation = 4.6 // Target angle when Artemis is active
  const hasReachedTarget = useRef(false)
  const artemisSpeedMultiplier = 120 // How much faster to spin when seeking target
  const currentSpeedMultiplier = useRef(1) // Current interpolated speed
  const speedTransitionSpeed = 5 // How fast to transition between speeds

  // Focus location rotation: same mechanism as Artemis
  const hasFocusReachedTarget = useRef(false)
  const prevFocusTargetRotation = useRef(null)
  const focusSpeedMultiplier = 80

  // Reusable Vector3 to avoid allocations every frame
  const earthPositionRef = useRef(new Vector3())
  
  // Reusable LOD update object to prevent recreation
  const lodUpdateRef = useRef({
    quality: fixedQuality,
    visibleCount: 0,
    totalTiles: tileGridConfig.totalTiles,
    isMobile
  })

  useFrame((state, delta) => {
    // Reset hasReachedTarget when exiting Artemis mode
    if (!artemisActive && hasReachedTarget.current) {
      hasReachedTarget.current = false
    }

    // Reset focus tracker when target changes (location focused or unfocused)
    if (focusTargetRotation !== prevFocusTargetRotation.current) {
      hasFocusReachedTarget.current = false
      prevFocusTargetRotation.current = focusTargetRotation
    }

    // Smoothly interpolate speed multiplier toward target.
    // Including focus path here means currentSpeedMultiplier ramps to 40 immediately — not just in slowdown zone.
    const targetMultiplier = (artemisActive && !hasReachedTarget.current)
      ? artemisSpeedMultiplier
      : (focusTargetRotation !== null && !hasFocusReachedTarget.current)
        ? focusSpeedMultiplier
        : 1
    const multiplierDiff = targetMultiplier - currentSpeedMultiplier.current
    if (Math.abs(multiplierDiff) > 0.01) {
      currentSpeedMultiplier.current += multiplierDiff * speedTransitionSpeed * delta
    } else {
      currentSpeedMultiplier.current = targetMultiplier
    }

    // Handle rotation based on Artemis mode
    // Use ref instead of state to avoid re-renders every frame
    if (autoRotate) {
      if (artemisActive && !hasReachedTarget.current) {
        // Earth always spins forward (+Y) toward the target. Measure the FORWARD
        // (positive) angular distance still to travel, normalised to [0, 2π).
        // Using the forward remaining-distance — rather than an unsigned "how close
        // am I now" threshold — is what makes arrival frame-rate independent: a slow
        // device can take a huge step, but as long as that step meets or exceeds the
        // remaining distance we snap exactly to the target that frame instead of
        // stepping over a fixed 0.01-rad window and spinning for another full lap.
        const normalizedRotation = ((spinRotationRef.current % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)
        const normalizedTarget = ((targetYRotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)
        let remaining = normalizedTarget - normalizedRotation
        if (remaining < 0) remaining += Math.PI * 2 // forward-only distance in [0, 2π)

        // Start slowing down within the last 1.0 radian of the approach.
        if (remaining < 1.0) {
          const inverse = 1 - remaining
          const easedFactor = 1 - (inverse * inverse * inverse)
          currentSpeedMultiplier.current = 1 + (artemisSpeedMultiplier - 1) * easedFactor
        }

        // This frame's forward step. Clamp it so it can never overshoot the target.
        const step = rotationSpeed * delta * currentSpeedMultiplier.current

        if (step >= remaining) {
          // Would reach or pass the target this frame — land exactly on it and latch,
          // no matter how large delta (i.e. how low the frame rate) is.
          spinRotationRef.current = targetYRotation
          hasReachedTarget.current = true
          if (onArtemisTargetReached) onArtemisTargetReached()
        } else {
          spinRotationRef.current += step
        }
      } else if (!artemisActive) {
        if (focusTargetRotation !== null && !hasFocusReachedTarget.current) {
          // ── Focus target mode ── spin to target angle then freeze (mirrors Artemis)
          const normalizedRotation = ((spinRotationRef.current % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)
          const normalizedTarget = ((focusTargetRotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)
          // Signed shortest-arc: positive = forward, negative = reverse
          let signedDiff = normalizedTarget - normalizedRotation
          if (signedDiff > Math.PI) signedDiff -= Math.PI * 2
          if (signedDiff < -Math.PI) signedDiff += Math.PI * 2
          const direction = Math.sign(signedDiff) || 1
          const diff = Math.abs(signedDiff)
          const newRotation = spinRotationRef.current + rotationSpeed * delta * currentSpeedMultiplier.current * direction

          if (diff < 1.0) {
            const inverse = 1 - (diff / 1.0)
            const easedFactor = 1 - (inverse * inverse * inverse)
            currentSpeedMultiplier.current = 1 + (focusSpeedMultiplier - 1) * easedFactor

            if (diff < 0.01) hasFocusReachedTarget.current = true
            spinRotationRef.current = diff < 0.001 ? focusTargetRotation : newRotation
          } else {
            spinRotationRef.current = newRotation
          }
        } else if (focusTargetRotation === null) {
          // ── Normal rotation ── only when no focus target is set
          spinRotationRef.current += rotationSpeed * delta * currentSpeedMultiplier.current
        }
        // If hasFocusReachedTarget: hold still — do not rotate
      }
      
      // Update the group rotation directly (no state update)
      if (innerGroupLocalRef.current) {
        innerGroupLocalRef.current.rotation.y = spinRotationRef.current
      }
      
      // Throttle parent rotation updates to reduce callbacks
      const now = state.clock.getElapsedTime() * 1000
      if (onRotationUpdate && now - lastRotationUpdateRef.current > ROTATION_UPDATE_THROTTLE) {
        lastRotationUpdateRef.current = now
        onRotationUpdate(spinRotationRef.current)
      }
    }

    // LOD SYSTEM DISABLED - Fixed to LOD_3. onLODUpdate removed (no consumer).
  })

  // Callback when loaded
  const handleLoad = () => {
    if (!isLoaded) {
      setIsLoaded(true)
      if (onLoad) {
        onLoad()
      }
    }
  }

  // Track when Earth is fully ready (materials loaded + rendered)
  const [isRendered, setIsRendered] = useState(false)
  
  useEffect(() => {
    if (materialsReady && !isRendered) {
      // Wait one frame to ensure geometry is rendered
      const timer = setTimeout(() => {
        setIsRendered(true)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [materialsReady, isRendered])
  
  // Report ready state to Scene Manager
  useSceneReady('earth', isRendered)

  // Don't render until materials are ready
  if (!materialsReady) {
    return null
  }

  return (
    <group ref={earthGroupRef} position={position} rotation={rotation}>
      <group ref={setInnerGroupRef}>
        <EarthSphere
          radius={radius}
          rotation={[0, 0, 0]}
          quality={currentQuality}
          position={[0, 0, 0]}
          materialCache={materialCache}
          sunPositionRef={sunPositionRef}
        />
        {enableAtmosphere && (
          <Atmosphere
            radius={radius}
            rotation={[0, 0, 0]}
            position={[0, 0, 0]}
            sunPositionRef={sunPositionRef}
            opacityOverrideRef={opacityOverrideRef}
          />
        )}
      </group>
    </group>
  )
}

export default memo(Earth)
